/**
 * Audio Pool Hook
 *
 * Manages a stable pool of <audio> elements for multi-track playback.
 * Creates audio elements once per clip on mount / clip list change,
 * then updates currentTime/volume/playbackRate on each playhead update.
 *
 * Key design:
 * - No teardown/rebuild cycle on playhead update
 * - Elements are stable — only created/removed when clips change
 * - Drift correction: re-seeks if >100ms off
 * - Uses blob URLs from preloader for instant loading
 *
 * RFC-0651 #11 follow-up: accepts a `mutedAudioTracks` set so per-row mute
 * actually silences audio-track clips (not just visual). Per-clip mute and
 * per-track mute compose with OR — if either is set, the clip is muted.
 */

import { useCallback, useMemo } from 'react'
import type { TimelineAudioClip } from './types'
import { audioFadeGain } from './audioFades'
import type { UseClipPreloaderReturn } from './useClipPreloader'
import { timelineAudioToEditClip } from '../../audio-edit/audioEditMath'
import { useAudioClipPool } from '../../audio-edit/useAudioClipPool'
import { createNadDebug } from '../../../../utils/debugLog'

const trackMuteLog = createNadDebug('dw:video-editor:track-mute')
// RFC-0631 (Issue 7): publish under the timeline-playback feature so the
// resolve-failure signal is co-located with the rest of the audio pool's
// telemetry. The missing observability is what allowed the silent-extracted-
// audio bug to ship; never regress this.
const audioPoolLog = createNadDebug('dw:timeline-playback:audio-pool')
const skipLoggedPaths = new Set<string>()

interface UseAudioPoolProps {
  audioClips: TimelineAudioClip[]
  playheadPosition: number
  isPlaying: boolean
  preloader: UseClipPreloaderReturn
  /** When true, every pooled <audio> element is forced to muted=true. */
  globalMuted?: boolean
  /**
   * RFC-0651 #11: indices of audio tracks that should be silenced. Composed
   * with the per-clip `muted` flag — either being true silences the clip.
   */
  mutedAudioTracks?: Set<number>
}

export interface TimelineAudioPoolHandle {
  resumeAudioContexts: () => void
}

export function useAudioPool({
  audioClips,
  playheadPosition,
  isPlaying,
  preloader,
  globalMuted = false,
  mutedAudioTracks,
}: UseAudioPoolProps): TimelineAudioPoolHandle {
  // Compose per-track mute into per-clip mute before handing off to the
  // underlying pool. We rebuild the array (not mutate) so the downstream
  // fingerprint correctly invalidates when a track flips its mute state.
  const effectiveClips = useMemo(() => {
    if (!mutedAudioTracks || mutedAudioTracks.size === 0) return audioClips
    let touched = 0
    const out = audioClips.map((clip) => {
      if (mutedAudioTracks.has(clip.trackIndex) && !clip.muted) {
        touched += 1
        return { ...clip, muted: true }
      }
      return clip
    })
    if (touched > 0) {
      trackMuteLog({
        event: 'compose-per-track-mute',
        mutedTracks: Array.from(mutedAudioTracks),
        clipsMutedByTrack: touched,
        totalAudioClips: audioClips.length,
      })
    }
    return out
  }, [audioClips, mutedAudioTracks])

  const editClips = useMemo(() => effectiveClips.map(timelineAudioToEditClip), [effectiveClips])
  const clipsById = useMemo(() => new Map(effectiveClips.map((clip) => [clip.id, clip])), [effectiveClips])
  const computeClipGain = useCallback((editClip: ReturnType<typeof timelineAudioToEditClip>, position: number) => {
    const clip = clipsById.get(editClip.id)
    return clip ? clip.volume * audioFadeGain(clip, position - clip.startOffset) : editClip.volume
  }, [clipsById])
  const resolveClipUrl = useCallback(
    (clip: ReturnType<typeof timelineAudioToEditClip>) => {
      if (!clip.sourcePath) return clip.sourceUrl ?? null
      const url = preloader.getBlobUrl(clip.sourcePath)
      if (!url) {
        // RFC-0631 (Issue 7): if we get here, the audio pool wanted to play
        // this clip but the preloader hasn't fetched its source yet. The
        // dominant cause is the path missing from the preloader's input
        // set — fix at the source rather than in the pool.
        const dedupeKey = `${clip.id}:${clip.sourcePath}`
        if (!skipLoggedPaths.has(dedupeKey)) {
          skipLoggedPaths.add(dedupeKey)
          audioPoolLog({
            event: 'audio-pool-skip-no-src',
            clipId: clip.id,
            sourcePath: clip.sourcePath,
          })
        }
      }
      return url
    },
    [preloader],
  )

  return useAudioClipPool({
    clips: editClips,
    playheadPosition,
    isPlaying,
    resolveClipUrl,
    globalMuted,
    computeClipGain,
  })
}
